import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import Button from 'WeployDesignLanguage/buttons/Button';
import Fade from 'WeployDesignLanguage/transitions/Fade';
import { DropDownIcon } from 'LegacyWeployDesignLanguage/icons';
import Menu, { MenuItem } from 'WeployDesignLanguage/Menu';
import dagstorp from 'LegacyWeployDesignLanguage/dagstorp';

import JobDetailsModal from './JobDetailsModal';
import { dateRangeToDuration } from '../../../utils/Date';

const ME = 'Me';

const TitleWrapper = styled.div`
  display: grid;
  align-items: center;
`;

const Title = styled.h5`
  margin: 0;
  padding-right: ${props => props.theme.spacing[1]};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const InnerNav = styled.nav`
  display: grid;
  grid-template-columns: ${props => props.theme.spacing[10]} minmax(0, 8fr) auto;
  grid-template-rows: auto auto;
`;

const ActionItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: ${props => props.theme.spacing[3]};
  & > :not(:first-child) {
    margin-left: ${props => props.theme.spacing[1]};
  }
  grid-area: 1 / 4 / 1 / 4;
`;

const JobSummary = styled.div`
  padding-top: ${props => props.theme.spacing[1]};
  grid-area: 2 / 2 / 3 / 3;
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    ${props => props.theme.typography.subtitle1};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const JobSummaryAndNavigation = ({
  backLinkRenderer,
  job,
  onTransferJobClick,
  onExtendJobClick,
  isTransferJobMenuItemVisible: showTransferJobMenuItem,
  isExtendJobMenuItemVisible: showExtendJobMenuItem,
}) => {
  const {
    startTime,
    finishTime,
    belongsToCurrentUser,
    owner,
    title,
  } = job;

  const startTimeMoment = moment(startTime);
  const finishTimeMoment = moment(finishTime);
  const ownerName = belongsToCurrentUser ? ME : owner;
  const [open, setOpen] = useState(false);
  const [menuAnchorElement, setMenuAnchorElement] = React.useState(null);
  const menuOpen = Boolean(menuAnchorElement);

  const iconColor = dagstorp.colors.text.primary;

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setMenuAnchorElement(event.currentTarget);
  };

  const handleMenuClose = () => (
    setMenuAnchorElement(null)
  );

  return (
    <Fragment>
      <JobDetailsModal
        job={job}
        open={open}
        onClose={() => { setOpen(false); }}
      />
      <InnerNav>
        { backLinkRenderer() }
        <TitleWrapper>
          <Title title={title}>{title}</Title>
        </TitleWrapper>

        <ActionItems>
          <Button
            onClick={handleMenuClick}
            appearance={Button.TEXT}
          >
            More Actions
            <DropDownIcon color={iconColor} />
          </Button>
        </ActionItems>
        <JobSummary>
          { job.numberOfPositions } { job.numberOfPositions === 1 ? 'Weployee' : 'Weployees' } • { job.city } • Owner: { ownerName }
          <br />
          { moment(startTimeMoment).format('dddd D MMMM h:mm a') } - { moment(finishTimeMoment).format('dddd D MMMM h:mm a') }  ({ dateRangeToDuration(startTimeMoment, finishTimeMoment) })
        </JobSummary>
        <Menu
          id="fade-menu"
          anchorEl={menuAnchorElement}
          keepMounted
          open={menuOpen}
          onClose={handleMenuClose}
          TransitionComponent={Fade}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <StyledMenuItem onClick={() => { setOpen(true); }}>View Job Details</StyledMenuItem>
          {
            showExtendJobMenuItem &&
            <StyledMenuItem onClick={() => onExtendJobClick(job.id)}>Extend Job</StyledMenuItem>
          }
          {
            showTransferJobMenuItem &&
              <StyledMenuItem onClick={() => onTransferJobClick(job.id)}>Transfer Job</StyledMenuItem>
          }
        </Menu>
      </InnerNav>
    </Fragment>
  );
};

JobSummaryAndNavigation.propTypes = {
  backLinkRenderer: PropTypes.func.isRequired,
  job: PropTypes.shape({
    belongsToCurrentUser: PropTypes.bool.isRequired,
    city: PropTypes.string,
    id: PropTypes.string,
    isTransferrable: PropTypes.bool.isRequired,
    owner: PropTypes.string,
    numberOfPositions: PropTypes.number,
    primarySkill: PropTypes.string,
    startTime: PropTypes.string,
    finishTime: PropTypes.string,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onTransferJobClick: PropTypes.func,
  onExtendJobClick: PropTypes.func,
  isTransferJobMenuItemVisible: PropTypes.bool.isRequired,
  isExtendJobMenuItemVisible: PropTypes.bool.isRequired,
};

JobSummaryAndNavigation.defaultProps = {
  onTransferJobClick: () => {},
  onExtendJobClick: () => {},
};

export default JobSummaryAndNavigation;
